import type { JSX } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import {
  DEVISES_MONTANT,
  FORMATS_DATE,
  TYPES_TRANSFORMATION,
  type FormatDate,
  type Transformation,
  type TypeTransformation,
} from '../../../../lib/correspondance.js';
import {
  dictionnaireEnTexte,
  refVide,
  texteEnDictionnaire,
  transformationParDefaut,
} from '../../../../lib/reglesEdition.js';
import { RefEditeur } from './RefEditeur.js';
import styles from './Correspondance.module.css';

interface Contexte {
  suggestions: readonly string[];
  roles: readonly string[];
  avecParent: boolean;
}

/**
 * Une étape de la liste fermée des transformations, avec ses paramètres. Le
 * type se change sur place (l'étape repart de ses valeurs par défaut) ;
 * monter, descendre et retirer déplacent l'étape dans la liste.
 */
export function TransformationEditeur({
  etape,
  rang,
  total,
  onChange,
  onDeplacer,
  onRetirer,
  contexte,
  formatManquant,
}: {
  etape: Transformation;
  rang: number;
  total: number;
  onChange: (suite: Transformation) => void;
  onDeplacer: (sens: -1 | 1) => void;
  onRetirer: () => void;
  contexte: Contexte;
  /** La colonne est une date ambiguë : cette étape doit déclarer son format. */
  formatManquant: boolean;
}): JSX.Element {
  const { t } = useTranslation('console');
  const id = useId();
  const nom = t(`correspondance.transformations.${etape.type}`);

  return (
    <li
      className={styles.etapeRegle}
      aria-label={t('correspondance.editeur.etapeNumero', { rang: rang + 1, nom })}
    >
      <div className={styles.etapeEntete}>
        <span className={styles.etapeRang} aria-hidden="true">
          {rang + 1}
        </span>
        <div className={styles.etapeType}>
          <label htmlFor={`${id}-type`}>{t('correspondance.editeur.typeEtape')}</label>
          <select
            id={`${id}-type`}
            className={styles.selection}
            value={etape.type}
            onChange={(evenement) =>
              onChange(transformationParDefaut(evenement.target.value as TypeTransformation))
            }
          >
            {TYPES_TRANSFORMATION.map((type) => (
              <option key={type} value={type}>
                {t(`correspondance.transformations.${type}`)}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.etapeGestes}>
          <Button
            variant="ghost"
            size="sm"
            disabled={rang === 0}
            aria-label={t('correspondance.editeur.monter', { nom })}
            onClick={() => onDeplacer(-1)}
          >
            {t('correspondance.editeur.monterCourt')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={rang >= total - 1}
            aria-label={t('correspondance.editeur.descendre', { nom })}
            onClick={() => onDeplacer(1)}
          >
            {t('correspondance.editeur.descendreCourt')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('correspondance.editeur.retirerEtape', { nom })}
            onClick={onRetirer}
          >
            {t('correspondance.editeur.retirer')}
          </Button>
        </div>
      </div>

      <p className={styles.aideEtape}>{t(`correspondance.transformationsAide.${etape.type}`)}</p>

      <div className={styles.parametres}>
        {etape.type === 'scinder' ? (
          <>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-sep`}>{t('correspondance.editeur.separateur')}</label>
              <Input
                id={`${id}-sep`}
                value={etape.separateur}
                maxLength={10}
                onChange={(e) => onChange({ ...etape, separateur: e.target.value })}
              />
            </div>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-index`}>{t('correspondance.editeur.indexScinder')}</label>
              <Input
                id={`${id}-index`}
                type="number"
                inputMode="numeric"
                min={-50}
                max={50}
                value={etape.index}
                onChange={(e) =>
                  onChange({ ...etape, index: Math.trunc(Number(e.target.value)) || 0 })
                }
              />
              <span className={styles.aideChamp}>{t('correspondance.editeur.indexAide')}</span>
            </div>
          </>
        ) : null}

        {etape.type === 'date' ? (
          <div className={styles.parametre}>
            <label htmlFor={`${id}-format`}>
              {t('correspondance.editeur.formatDate')}
              {formatManquant ? ` (${t('correspondance.editeur.obligatoire')})` : ''}
            </label>
            <select
              id={`${id}-format`}
              className={styles.selection}
              value={etape.format}
              onChange={(e) => onChange({ ...etape, format: e.target.value as FormatDate })}
            >
              {FORMATS_DATE.map((format) => (
                <option key={format} value={format}>
                  {t(`correspondance.formatsDate.${format}`)}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {etape.type === 'montant' ? (
          <>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-decimal`}>{t('correspondance.editeur.decimal')}</label>
              <select
                id={`${id}-decimal`}
                className={styles.selection}
                value={etape.decimal}
                onChange={(e) =>
                  onChange({ ...etape, decimal: e.target.value === '.' ? '.' : ',' })
                }
              >
                <option value=",">{t('correspondance.editeur.decimalVirgule')}</option>
                <option value=".">{t('correspondance.editeur.decimalPoint')}</option>
              </select>
            </div>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-devise`}>{t('correspondance.editeur.devise')}</label>
              <select
                id={`${id}-devise`}
                className={styles.selection}
                value={etape.devise ?? ''}
                onChange={(e) => {
                  const { devise: _ancienne, ...reste } = etape;
                  const suite = e.target.value as (typeof DEVISES_MONTANT)[number] | '';
                  onChange(suite === '' ? reste : { ...reste, devise: suite });
                }}
              >
                <option value="">{t('correspondance.editeur.deviseAucune')}</option>
                {DEVISES_MONTANT.map((devise) => (
                  <option key={devise} value={devise}>
                    {devise}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : null}

        {etape.type === 'dictionnaire' ? (
          <>
            <div className={`${styles.parametre} ${styles.parametreLarge}`}>
              <label htmlFor={`${id}-valeurs`}>{t('correspondance.editeur.dictionnaire')}</label>
              <textarea
                id={`${id}-valeurs`}
                className={styles.zoneTexte}
                rows={4}
                spellCheck={false}
                defaultValue={dictionnaireEnTexte(etape.valeurs)}
                onChange={(e) =>
                  onChange({ ...etape, valeurs: texteEnDictionnaire(e.target.value) })
                }
              />
              <span className={styles.aideChamp}>
                {t('correspondance.editeur.dictionnaireAide')}
              </span>
            </div>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-defaut`}>{t('correspondance.editeur.valeurParDefaut')}</label>
              <Input
                id={`${id}-defaut`}
                value={etape.defaut ?? ''}
                onChange={(e) => {
                  const { defaut: _ancien, ...reste } = etape;
                  onChange(e.target.value === '' ? reste : { ...reste, defaut: e.target.value });
                }}
              />
            </div>
          </>
        ) : null}

        {etape.type === 'constante' || etape.type === 'prefixe' ? (
          <div className={styles.parametre}>
            <label htmlFor={`${id}-valeur`}>{t('correspondance.editeur.valeur')}</label>
            <Input
              id={`${id}-valeur`}
              value={etape.valeur}
              onChange={(e) => onChange({ ...etape, valeur: e.target.value })}
            />
          </div>
        ) : null}

        {etape.type === 'si_vide' ? (
          <>
            <div className={styles.parametre}>
              <label htmlFor={`${id}-mode`}>{t('correspondance.editeur.siVideMode')}</label>
              <select
                id={`${id}-mode`}
                className={styles.selection}
                value={etape.source ? 'colonne' : 'valeur'}
                onChange={(e) =>
                  onChange(
                    e.target.value === 'colonne'
                      ? { type: 'si_vide', source: refVide() }
                      : { type: 'si_vide', valeur: '' },
                  )
                }
              >
                <option value="valeur">{t('correspondance.editeur.siVideValeur')}</option>
                <option value="colonne">{t('correspondance.editeur.siVideColonne')}</option>
              </select>
            </div>
            {etape.source ? (
              <RefEditeur
                valeur={etape.source}
                libelle={t('correspondance.editeur.colonne')}
                suggestions={contexte.suggestions}
                roles={contexte.roles}
                avecParent={contexte.avecParent}
                onChange={(source) => onChange({ type: 'si_vide', source })}
              />
            ) : (
              <div className={styles.parametre}>
                <label htmlFor={`${id}-valeur`}>{t('correspondance.editeur.valeur')}</label>
                <Input
                  id={`${id}-valeur`}
                  value={etape.valeur ?? ''}
                  onChange={(e) => onChange({ type: 'si_vide', valeur: e.target.value })}
                />
              </div>
            )}
          </>
        ) : null}

        {etape.type === 'concat' ? (
          <>
            <RefEditeur
              valeur={etape.source}
              libelle={t('correspondance.editeur.colonneAjoutee')}
              suggestions={contexte.suggestions}
              roles={contexte.roles}
              avecParent={contexte.avecParent}
              onChange={(source) => onChange({ ...etape, source })}
            />
            <div className={styles.parametre}>
              <label htmlFor={`${id}-sep`}>{t('correspondance.editeur.separateur')}</label>
              <Input
                id={`${id}-sep`}
                value={etape.separateur}
                maxLength={10}
                onChange={(e) => onChange({ ...etape, separateur: e.target.value })}
              />
            </div>
          </>
        ) : null}
      </div>
    </li>
  );
}
